import React, { useEffect, useRef, useState } from 'react'
import { DocumentDownload } from 'iconsax-react'
import styled from 'styled-components'

import Button from 'components/lib/DesignSystem/V3/Button'

import ShareNodes from './icons/ShareNodes'

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--t-spacing-4);
  margin-bottom: var(--t-spacing-6);
`

const MainTitle = styled.h1`
  margin: 0 0 var(--t-spacing-2);
  font-size: var(--t-typography-font-size-xl);
  font-weight: var(--t-typography-font-weight-semi-bold);
  line-height: var(--t-typography-line-height-xl);
  color: var(--color-text);
`

const SubTitle = styled.p`
  margin: 0;
  font-size: var(--t-typography-font-size-sm);
  line-height: var(--t-typography-line-height-sm);
  color: var(--sub-text-color);
`

/* Same menu as the training report's Share / Actions: anchored under the button, one
   item per line with a leading icon. A single item today, more as export options land. */
const Menu = styled.div`
  position: relative;
`

const MenuPop = styled.div`
  position: absolute;
  top: calc(100% + var(--t-spacing-1-5));
  right: 0;
  z-index: 30;
  min-width: 220px;
  padding: var(--t-spacing-1-5);
  background: var(--color-elevated);
  border: 1px solid var(--color-elevated-border);
  border-radius: var(--t-border-radius-lg);
  box-shadow: var(--tooltip-shadow);
`

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: var(--t-spacing-2-5);
  width: 100%;
  padding: var(--t-spacing-2) var(--t-spacing-2-5);
  border: none;
  border-radius: var(--t-border-radius-md);
  background: none;
  cursor: pointer;
  font: inherit;
  font-size: var(--t-typography-font-size-sm);
  color: var(--color-text);
  text-align: left;
  white-space: nowrap;

  &:hover {
    background: var(--dropdown-hover, var(--color-background));
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const TopSection = ({ onDownloadCsv, downloadDisabled = false }) => {
  const [open, setOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const close = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false)
    }
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const handleDownload = () => {
    setOpen(false)
    onDownloadCsv?.()
  }

  return (
    <Header>
      <div>
        <MainTitle>Launched simulations</MainTitle>
        <SubTitle>Track generation and deployment progress for each simulation</SubTitle>
      </div>
      <Menu ref={menuRef}>
        <Button
          id="Psima_Launched_Share_Button"
          dataTestId="Psima_Launched_Share_Button"
          variant="secondary"
          size="medium"
          ariaHasPopup="menu"
          ariaExpanded={open}
          leftElement={<ShareNodes size={16} />}
          onClick={() => setOpen((value) => !value)}
        >
          Share
        </Button>
        {open && (
          <MenuPop role="menu" data-testid="Psima_Launched_Share_menu">
            <MenuItem
              type="button"
              role="menuitem"
              id="Psima_Launched_DownloadAll_Button"
              data-testid="Psima_Launched_DownloadAll_Button"
              disabled={downloadDisabled}
              title="Downloads all launched simulations. Filters don't apply."
              onClick={handleDownload}
            >
              <DocumentDownload size={16} variant="Outline" />
              Download CSV
            </MenuItem>
          </MenuPop>
        )}
      </Menu>
    </Header>
  )
}

export default TopSection
